import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { toast } from "@volca/ui/lib/toast";
import { count } from "@volca/ui/trades";
import { reportError } from "@/profitability/utils";
import { EXPLAIN_REASONS, FeedbackDialog } from "./feedback-dialog";
import { isActive } from "./labels";
import { useUpdateFindings } from "./query";
import type { Finding, FindingResolution, FindingStatus } from "./types";

/**
 * A rule's findings, acted on together: ask Volca about them, mark them
 * investigating or fixed, or explain them with one reason. Only the ones
 * still active move.
 */
export const GroupActions = ({
  businessId,
  rule,
  findings,
  noun,
  plural,
  onAsk,
  bulk = true,
}: {
  businessId: string;
  rule: string;
  findings: Finding[];
  noun: string;
  plural: string;
  /** Ask Volca about the group, from the same menu as the rest. */
  onAsk?: () => void;
  /**
   * Mark and explain the findings in `findings`. A preview of a rule leaves
   * this off: those actions would claim to cover the rule and only touch
   * the rows on screen.
   */
  bulk?: boolean;
}) => {
  const update = useUpdateFindings(businessId);
  const [explaining, setExplaining] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = findings.filter((finding) => isActive(finding.status));
  const each = async (
    change: {
      status?: FindingStatus;
      resolution?: FindingResolution;
      note?: string;
    },
    done: string,
  ) => {
    setBusy(true);
    try {
      await update.mutateAsync({
        findingIds: active.map((finding) => finding.id),
        ...change,
      });
      toast.success(`${count(active.length, noun, plural)} ${done}`);
      setExplaining(false);
    } catch (error) {
      toast.error("Some findings could not be updated", {
        description: reportError(error),
      });
    } finally {
      setBusy(false);
    }
  };
  if (!onAsk && (!bulk || !active.length)) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              loading={busy}
              loadingLabel="Updating findings"
              aria-label={`Actions for every ${rule} ${noun}`}
            />
          }
        >
          <DotsThreeIcon aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          {onAsk && (
            <DropdownMenuItem onClick={onAsk}>
              Ask Volca about these
            </DropdownMenuItem>
          )}
          {onAsk && bulk && active.length > 0 && <DropdownMenuSeparator />}
          {bulk && active.length > 0 && (
            <>
              <DropdownMenuItem
                onClick={() =>
                  void each({ status: "investigating" }, "marked investigating")
                }
              >
                Mark all investigating
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setExplaining(true)}>
                Explain all…
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  void each(
                    { status: "resolved", resolution: "fixed" },
                    "marked fixed",
                  )
                }
              >
                Mark all fixed
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <FeedbackDialog
        open={explaining}
        onOpenChange={setExplaining}
        title={`Why are these ${count(active.length, noun, plural)} explained?`}
        reasons={EXPLAIN_REASONS}
        submitLabel="Mark explained"
        pending={busy}
        onSubmit={({ reason, note }) =>
          void each(
            {
              status: "explained",
              resolution: reason as FindingResolution,
              note: note || undefined,
            },
            "explained",
          )
        }
      />
    </>
  );
};
